// Stan autoryzacji: JWT trzymany w localStorage + reaktywny useState.
// API jest SPA (ssr: false), więc localStorage jest zawsze dostępny po stronie klienta.
import { language } from '@/composables/useLanguage.js'

const TOKEN_KEY = 'rountine_token'

export const useAuth = () => {
    const config = useRuntimeConfig()
    const token = useState<string | null>('auth.token', () => null)

    // Hydratacja ze storage przy pierwszym użyciu (np. po odświeżeniu strony)
    if (import.meta.client && token.value === null) {
        token.value = localStorage.getItem(TOKEN_KEY)
    }

    const isAuthenticated = computed(() => !!token.value)

    // Email użytkownika wyciągany z payloadu JWT (claim "username").
    const userEmail = computed<string | null>(() => {
        if (!token.value) return null
        try {
            const payload = token.value.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
            return JSON.parse(atob(payload)).username ?? null
        } catch {
            return null
        }
    })

    // Inicjały na awatar — pierwsze dwie litery z części lokalnej maila.
    const userInitials = computed(() => {
        const email = userEmail.value
        if (!email) return '?'
        const letters = email.split('@')[0].replace(/[^a-zA-Z]/g, '')
        return (letters.slice(0, 2) || email.slice(0, 2)).toUpperCase()
    })

    const setToken = (value: string | null) => {
        token.value = value
        if (import.meta.client) {
            if (value) localStorage.setItem(TOKEN_KEY, value)
            else localStorage.removeItem(TOKEN_KEY)
        }
    }

    const login = async (email: string, password: string) => {
        const res = await $fetch<{ token: string }>('/login', {
            baseURL: config.public.apiHost,
            // przed zalogowaniem API nie zna konta — komunikaty w języku ekranu
            headers: { 'Accept-Language': language.value },
            method: 'POST',
            body: { email, password },
        })
        setToken(res.token)
        return res
    }

    // Język przeglądarki — z niego API ustala walutę nowego konta.
    const locale = () => (typeof navigator === 'undefined' ? null : navigator.language || null)

    // Logowanie przez Google: ID token z przycisku Google wymieniany w API na nasz JWT.
    // Nieznane konto Google zakłada nowe konto (`created`) z `locale` przeglądarki i językiem ekranu.
    const loginWithGoogle = async (credential: string) => {
        const res = await $fetch<{ token: string, created: boolean }>('/auth/google', {
            baseURL: config.public.apiHost,
            // przed zalogowaniem API nie zna konta — komunikaty w języku ekranu
            headers: { 'Accept-Language': language.value },
            method: 'POST',
            body: { credential, locale: locale(), language: language.value },
        })
        setToken(res.token)
        return res
    }

    const register = async (email: string, password: string) => {
        return $fetch('/register', {
            baseURL: config.public.apiHost,
            // przed zalogowaniem API nie zna konta — komunikaty w języku ekranu
            headers: { 'Accept-Language': language.value },
            method: 'POST',
            body: { email, password, locale: locale(), language: language.value },
        })
    }

    // Krok 1 resetu hasła: prośba o wysłanie 6-cyfrowego kodu na maila.
    // Backend zawsze zwraca 200 ("jeśli konto istnieje..."), 429 przy zbyt wielu żądaniach.
    const requestPasswordReset = async (email: string) => {
        return $fetch('/password-reset/request', {
            baseURL: config.public.apiHost,
            // przed zalogowaniem API nie zna konta — komunikaty w języku ekranu
            headers: { 'Accept-Language': language.value },
            method: 'POST',
            body: { email },
        })
    }

    // Krok 2 resetu hasła: potwierdzenie kodem i ustawienie nowego hasła.
    // 400 gdy kod nieprawidłowy lub wygasły.
    const confirmPasswordReset = async (email: string, code: string, password: string) => {
        return $fetch('/password-reset/confirm', {
            baseURL: config.public.apiHost,
            // przed zalogowaniem API nie zna konta — komunikaty w języku ekranu
            headers: { 'Accept-Language': language.value },
            method: 'POST',
            body: { email, code, password },
        })
    }

    // Dokąd po zalogowaniu: `?next=` tylko jako ścieżka w aplikacji (nie inna domena).
    const afterLogin = (query: unknown) => {
        const next = String(query ?? '')
        return next.startsWith('/') && !next.startsWith('//') ? next : '/'
    }

    const logout = async () => {
        setToken(null)
        await navigateTo('/login')
    }

    return { token, isAuthenticated, userEmail, userInitials, login, loginWithGoogle, register, requestPasswordReset, confirmPasswordReset, logout, setToken, afterLogin }
}
